// ---------------------------------------------------------------------------
// AddStepLine — Hover-reveal [+] button with searchable action popover.
// ---------------------------------------------------------------------------

import { useState, useMemo } from 'react'
import {
  Plus,
  Search,
  Database,
  Variable,
  Zap,
  GitBranch,
  ArrowRight,
  MessageSquare,
  Code,
  Shield,
  Repeat,
  GitFork,
  Layers,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { FlowStep } from '@cogo/types-v2'
import { ACTION_DEFS } from '@cogo/action-registry'
import type { ActionCategory, ActionDef as RegistryActionDef, ActionParamDef } from '@cogo/action-registry'
import { cn } from '@/lib/utils'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { dispatchFlowStepsOp } from '@/features/flow-editor/lib/flow-steps-dispatch'
import type { NestedStepPath, StepBranchPath } from '@/features/flow-editor/store/flow-slice'

// ---------------------------------------------------------------------------
// Action definitions
// ---------------------------------------------------------------------------

interface ActionDef {
  action: string
  label: string
  category: ActionCategory | 'control'
  icon: LucideIcon
  paramCount: number
  defaultParams: Record<string, unknown>
  description?: string
  registrySource?: 'action-registry' | 'structural-step'
}

// Exported for the catalog<->runtime parity gate (add-step-popover-parity.test.ts):
// every registry-backed entry is generated from @cogo/action-registry ACTION_DEFS.
// Deprecated entries are hidden. This keeps the picker as a data-driven catalog
// instead of a second hand-maintained action list that drifts from runtime.

const ACTION_ICON_BY_CATEGORY: Record<ActionDef['category'], LucideIcon> = {
  state: Database,
  data: Database,
  network: Zap,
  navigation: ArrowRight,
  ui: MessageSquare,
  system: Code,
  crypto: Code,
  storage: Database,
  media: Code,
  flow: GitBranch,
  form: Variable,
  auth: Shield,
  ai: Zap,
  realtime: Zap,
  control: GitBranch,
}

const CATEGORY_CONFIG: Record<ActionDef['category'], { label: string; className: string }> = {
  state: { label: 'State', className: 'bg-primary/15 text-primary dark:text-primary' },
  data: { label: 'Data', className: 'bg-primary/15 text-primary dark:text-primary' },
  network: { label: 'Network', className: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400' },
  navigation: { label: 'Navigation', className: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  ui: { label: 'UI', className: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
  system: { label: 'System', className: 'bg-slate-500/15 text-slate-600 dark:text-slate-300' },
  crypto: { label: 'Crypto', className: 'bg-slate-500/15 text-slate-600 dark:text-slate-300' },
  storage: { label: 'Storage', className: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  media: { label: 'Media', className: 'bg-pink-500/15 text-pink-600 dark:text-pink-400' },
  flow: { label: 'Flow', className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  form: { label: 'Form', className: 'bg-lime-500/15 text-lime-700 dark:text-lime-400' },
  auth: { label: 'Auth', className: 'bg-rose-500/15 text-rose-600 dark:text-rose-400' },
  ai: { label: 'AI', className: 'bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400' },
  realtime: { label: 'Realtime', className: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400' },
  control: { label: 'Flow Control', className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
}

const CATEGORY_ORDER: Array<ActionDef['category']> = [
  'state',
  'data',
  'network',
  'navigation',
  'ui',
  'form',
  'flow',
  'control',
  'storage',
  'auth',
  'realtime',
  'system',
  'media',
  'crypto',
  'ai',
]

const RUNTIME_PREFERRED_PARAM_OVERRIDES: Record<string, Record<string, unknown>> = {
  setState: { target: '', value: '' },
  navigate: { route: '' },
  callFlow: { flowName: '' },
  restApi: { url: '', method: 'GET', resultTarget: '' },
  showToast: { message: '', variant: 'success' },
}

const STRUCTURAL_ACTIONS: ActionDef[] = [
  { action: 'conditional', label: 'If / Else', category: 'control', icon: GitBranch, paramCount: 1, defaultParams: {}, registrySource: 'structural-step' },
  { action: 'tryCatch', label: 'Try / Catch / Finally', category: 'control', icon: Shield, paramCount: 0, defaultParams: {}, registrySource: 'structural-step' },
  { action: 'loop', label: 'Loop', category: 'control', icon: Repeat, paramCount: 2, defaultParams: { collection: '', itemVar: 'item' }, registrySource: 'structural-step' },
  { action: 'switch', label: 'Switch', category: 'control', icon: GitFork, paramCount: 1, defaultParams: { value: '' }, registrySource: 'structural-step' },
  { action: 'parallel', label: 'Parallel', category: 'control', icon: Layers, paramCount: 0, defaultParams: {}, registrySource: 'structural-step' },
  { action: 'stage', label: 'Stage', category: 'control', icon: Layers, paramCount: 1, defaultParams: { name: 'newStage' }, description: 'Ordered lifecycle boundary with optional hooks and compensation.', registrySource: 'structural-step' },
  { action: 'saga', label: 'Saga', category: 'control', icon: Shield, paramCount: 0, defaultParams: {}, description: 'Ordered stages with reverse compensation on failure.', registrySource: 'structural-step' },
]

function defaultValueForParam(param: ActionParamDef): unknown {
  if (param.default !== undefined) return param.default
  if (param.type === 'number') return 0
  if (param.type === 'boolean') return false
  if (param.type === 'object') return {}
  if (param.type === 'array') return []
  return ''
}

function defaultParamsFromRegistry(def: RegistryActionDef): Record<string, unknown> {
  const overridden = RUNTIME_PREFERRED_PARAM_OVERRIDES[def.name]
  if (overridden) return overridden

  return Object.fromEntries(
    def.params
      .filter((param) => param.required === true || param.default !== undefined)
      .map((param) => [param.name, defaultValueForParam(param)]),
  )
}

function actionFromRegistry(def: RegistryActionDef): ActionDef {
  const defaultParams = defaultParamsFromRegistry(def)
  return {
    action: def.name,
    label: def.label,
    category: def.category,
    icon: ACTION_ICON_BY_CATEGORY[def.category] ?? Database,
    paramCount: Object.keys(defaultParams).length,
    defaultParams,
    description: def.description,
    registrySource: 'action-registry',
  }
}

function buildActionsFromRegistry(): ActionDef[] {
  const registryActions = Object.values(ACTION_DEFS)
    .filter((def) => def.platform.web && def.deprecated !== true)
    .map(actionFromRegistry)
    .sort((a, b) => {
      const catDelta = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
      if (catDelta !== 0) return catDelta
      return a.label.localeCompare(b.label)
    })

  return [...registryActions, ...STRUCTURAL_ACTIONS]
}

export const ACTIONS: ActionDef[] = buildActionsFromRegistry()

// ---------------------------------------------------------------------------
// Build a FlowStep from an ActionDef
// ---------------------------------------------------------------------------

function cloneDefaultParam(value: unknown): unknown {
  if (Array.isArray(value)) return [...value]
  if (value && typeof value === 'object') return { ...(value as Record<string, unknown>) }
  return value
}

function buildStepFromAction(def: ActionDef): FlowStep {
  if (def.action === 'conditional') {
    return { type: 'conditional', condition: '', then: [], else: [] } as unknown as FlowStep
  }
  if (def.action === 'tryCatch') {
    // Start with safe try/finally semantics. An empty catch would suppress a
    // failure before the user has added an actual handler.
    return { type: 'tryCatch', try: [], finally: [] } as unknown as FlowStep
  }
  if (def.action === 'loop') {
    return { type: 'loop', collection: '', itemVar: 'item', steps: [] } as unknown as FlowStep
  }
  if (def.action === 'switch') {
    return { type: 'switch', value: '', cases: {}, default: [] } as unknown as FlowStep
  }
  if (def.action === 'parallel') {
    return { type: 'parallel', steps: [] } as unknown as FlowStep
  }
  if (def.action === 'stage') {
    return { type: 'stage', name: 'newStage', steps: [] } as unknown as FlowStep
  }
  if (def.action === 'saga') {
    return {
      type: 'saga',
      stages: [{ type: 'stage', name: 'stage1', steps: [] }],
    } as unknown as FlowStep
  }
  return {
    action: def.action,
    params: Object.fromEntries(
      Object.entries(def.defaultParams).map(([key, value]) => [key, cloneDefaultParam(value)]),
    ),
  } as unknown as FlowStep
}

// ---------------------------------------------------------------------------
// ActionSearchPopover
// ---------------------------------------------------------------------------

function ActionSearchPopover({
  onSelect,
  allowedActions,
}: {
  onSelect: (def: ActionDef) => void
  allowedActions?: readonly string[]
}) {
  const [search, setSearch] = useState('')
  const q = search.toLowerCase()

  const grouped = useMemo(() => {
    const available = allowedActions
      ? ACTIONS.filter((action) => allowedActions.includes(action.action))
      : ACTIONS
    const filtered = q
      ? available.filter((a) =>
          a.action.toLowerCase().includes(q) ||
          a.label.toLowerCase().includes(q) ||
          a.description?.toLowerCase().includes(q),
        )
      : available

    const groups = CATEGORY_ORDER.reduce((acc, cat) => {
      acc[cat] = []
      return acc
    }, {} as Record<ActionDef['category'], ActionDef[]>)
    for (const a of filtered) {
      groups[a.category].push(a)
    }
    return groups
  }, [q, allowedActions])

  const hasResults = CATEGORY_ORDER.some((cat) => grouped[cat].length > 0)

  return (
    <div className="flex flex-col max-h-[320px]">
      {/* Search input */}
      <div className="relative p-2 border-b border-border">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
        <input
          className="w-full h-7 pl-7 pr-2 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
          placeholder="Search actions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      {/* Action list */}
      <div className="overflow-y-auto p-1">
        {!hasResults && (
          <div className="px-3 py-4 text-xs text-center text-muted-foreground">
            No actions match &quot;{search}&quot;
          </div>
        )}
        {CATEGORY_ORDER.map((cat) => {
          const actions = grouped[cat]
          if (actions.length === 0) return null
          const config = CATEGORY_CONFIG[cat]

          return (
            <div key={cat} className="mb-1">
              <div className={cn('px-2 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-sm', config.className)}>
                {config.label}
              </div>
              {actions.map((def) => {
                const Icon = def.icon
                return (
                  <button
                    key={def.action}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded-sm hover:bg-accent transition-colors"
                    onClick={() => onSelect(def)}
                  >
                    <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{def.label}</span>
                    {def.paramCount > 0 && (
                      <span className="ml-auto text-[9px] text-muted-foreground shrink-0">
                        {def.paramCount}p
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// AddStepLine
// ---------------------------------------------------------------------------

interface AddStepLineProps {
  index: number
  label?: string
  alwaysVisible?: boolean
  /** Optional: full path of the nested array context (the owning step's path
   *  followed by the branch name, any depth). If provided, the step is
   *  inserted into that nested array rather than top-level. */
  nestedParent?: StepBranchPath
  /** Restrict structurally typed branches such as Saga.stages. */
  allowedActions?: readonly string[]
}

export function AddStepLine({ index, label, alwaysVisible = false, nestedParent, allowedActions }: AddStepLineProps) {
  const [open, setOpen] = useState(false)
  const setSelectedStepIndex = useFlowEditorStore((s) => s.setSelectedStepIndex)
  const setSelectedStepPath = useFlowEditorStore((s) => s.setSelectedStepPath)
  const setInspectorOpen = useFlowEditorStore((s) => s.setInspectorOpen)

  function handleSelect(def: ActionDef) {
    const step = buildStepFromAction(def)
    // Inserts dispatch through the MCP bus so the undo middleware records
    // them (F4). The mutator runs SYNCHRONOUSLY inside the un-awaited
    // dispatch (see flow-steps-dispatch.ts), so the selection updates below
    // stay synchronous — the inspector shows the new step's form in the same
    // render pass (#986 QA-2a).
    if (nestedParent) {
      const path = [...nestedParent, index] as unknown as NestedStepPath
      dispatchFlowStepsOp('insertNestedStep', [path, step])
      setSelectedStepPath(path)
      setInspectorOpen(true)
    } else {
      dispatchFlowStepsOp('addStep', [index, step])
      setSelectedStepIndex(index)
      setSelectedStepPath([index])
      setInspectorOpen(true)
    }
    setOpen(false)
  }

  return (
    <div className={cn('group relative flex items-center justify-center', alwaysVisible ? 'h-8' : 'h-6 -my-1')}>
      <div className="absolute inset-x-3 h-px bg-border opacity-0 transition-opacity group-hover:opacity-100" />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Add step at position ${index + 1}`}
            title={`Add step at position ${index + 1}`}
            data-testid={
              alwaysVisible
                ? 'add-first-step-button'
                : nestedParent
                  ? 'add-nested-step-button'
                  : 'add-top-level-step-button'
            }
            data-step-index={index}
            data-step-branch-path={nestedParent?.join('.') ?? undefined}
            className={cn(
              'relative z-10 flex items-center justify-center bg-primary text-xs text-primary-foreground transition-opacity focus:outline-none focus:ring-2 focus:ring-ring',
              alwaysVisible
                ? 'h-7 gap-1.5 rounded-md px-3 font-medium opacity-100 hover:bg-primary/90'
                : 'h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 focus:opacity-100',
            )}
          >
            <Plus className="w-3 h-3" />
            {label && <span>{label}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="center">
          <ActionSearchPopover onSelect={handleSelect} allowedActions={allowedActions} />
        </PopoverContent>
      </Popover>
    </div>
  )
}
